import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import actions from "@/actions";
import {
  UPDATE_SCHEDULE_TARGET,
  UPDATE_SCHEDULE_WITH_PROCESS_TARGET,
  UPDATE_SCHEDULE_WITH_TASK_TARGET,
} from "@/components/list/Schedule/constants";
import { ScheduleItem } from "@/components/list/Schedule/types";
import { UpdateScheduleRequest } from "@/components/list/Schedule/types/api";
import { formatScheduleValue } from "@/lib/common";
import storageManager from "@/lib/storageManager";
import { RootState } from "@/reducers/types";
import { isValidOvertimeFormat, isValidOvertimeRange } from "@/lib/validate";

export const useTextEditor = ({ onSearch }: { onSearch: (isPartialUpdate?: boolean) => void }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [item, setItem] = useState<ScheduleItem>();
  const [target, setTarget] = useState("");

  // 予定表項目情報
  const scheduleFields = useSelector((state: RootState) => state.schedule.fields);
  const targetLabel = useMemo(() => {
    const locale = storageManager.getLocale();
    const field = scheduleFields.find((field) => field.field_key === target);
    const fieldLabel = locale === "ja" ? field?.field_label_jp : field?.field_label_en;
    if (fieldLabel) return fieldLabel;

    switch (target) {
      case "field3": // 施工会社
        return t("construction_attend") + t("company_title");
      case "field4": // 危険源
        return t("hazard_source");
      case "field5": // 低減策
        return t("hazard_reduction_activity");
      case "field_t1": // 人数
        return t("number_of_workers");
      case "field_t4": // 使用火気
        return t("use_flame");
      case "field_t7": // 重機
        return t("heavy_machine");
      case "field_t8": // 安全指示
        return t("safety") + t("instruction");
      case "field_t9": // 残業予定時間
        return t("schedule_overtime_hour");
      case "note1": // 工事主管課コメント
        return `${t("construction")}${t("supervising_section")}${t("comments")}`;
      case "note2": // 設備主管課コメント
        return `${t("facility")}${t("supervising_section")}${t("comments")}`;
      case "note3": // 元請コメント
        return `${t("primary_charge")}${t("comments")}`;
      default:
        return "";
    }
  }, [scheduleFields, target, t]);

  const enter = (props: { item: ScheduleItem; target: string }) => {
    setItem(props.item);
    setTarget(props.target);
  };

  const exit = () => {
    setItem(null);
    setTarget(null);
  };

  const submit = (value?: string, e?: HTMLTextAreaElement) => {
    // バリデーション
    if (["note1", "note2", "note3"].includes(target) && 500 < (value || "").length) {
      dispatch(
        actions.app.showAlert(t("error"), [t("is_too_long_max", { field: targetLabel, length: 500 })], () =>
          dispatch(actions.app.hideAlert())
        )
      );
      e?.focus();
      return;
    }
    if (
      ["field3", "field4", "field5", "field_t4", "field_t7", "field_t8"].includes(target) &&
      100 < (value || "").length
    ) {
      dispatch(
        actions.app.showAlert(t("error"), [t("is_too_long_max", { field: targetLabel, length: 100 })], () =>
          dispatch(actions.app.hideAlert())
        )
      );
      e?.focus();
      return;
    }
    if (target === "field_t9" && value) {
      const num = Number(value);
      if (!Number.isFinite(num)) {
        dispatch(
          actions.app.showAlert(t("error"), [t("invalid_number_format", { field: targetLabel })], () =>
            dispatch(actions.app.hideAlert())
          )
        );
        e?.focus();
        return;
      }
      if (!isValidOvertimeFormat(value)) {
        dispatch(
          actions.app.showAlert(t("error"), [t("warn_number_format_two_decimal_digits", { field: targetLabel })], () =>
            dispatch(actions.app.hideAlert())
          )
        );
        e?.focus();
        return;
      }
      if (!isValidOvertimeRange(value)) {
        dispatch(
          actions.app.showAlert(t("error"), [t("warn_number_range_0_to_99_99", { field: targetLabel })], () =>
            dispatch(actions.app.hideAlert())
          )
        );
        e?.focus();
        return;
      }
    }
    if (target === "field_t1" && value) {
      const num = Number(value);
      if (!(Number.isSafeInteger(num) && 0 <= num)) {
        dispatch(
          actions.app.showAlert(t("error"), [t("invalid_number_format", { field: targetLabel })], () =>
            dispatch(actions.app.hideAlert())
          )
        );
        e?.focus();
        return;
      }
    }

    // 更新
    const request: UpdateScheduleRequest = {
      input: {
        schedule_task_id: item.schedule_task_id,
        target,
        value: formatScheduleValue(target, value),
        update_date: item[`${target}_update_date`] ?? item.timestamp.update_date,
      },
      diff_number_of_workers: diffNumberOfWorkers(value),
      onSuccess:
        target === "field_t9"
          ? () => {
              onSearch(true);
            }
          : undefined,
    };
    if (UPDATE_SCHEDULE_TARGET.includes(target) || UPDATE_SCHEDULE_WITH_PROCESS_TARGET.includes(target)) {
      dispatch(actions.schedule.updateSchedule(request));
    } else if (UPDATE_SCHEDULE_WITH_TASK_TARGET.includes(target)) {
      dispatch(actions.schedule.updateScheduleWithTask(request));
    }

    exit();
  };

  const diffNumberOfWorkers = (v?: string | number): number => {
    if (target !== "field_t1") return 0;
    const current = Number(item.field_t1 || 0);
    const newValue = isNaN(Number(v)) ? 0 : Number(v || 0);

    return newValue - current;
  };

  return { id: item?.schedule_task_id, target, enter, exit, submit };
};
